/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */

import moment from "moment";

/**
 * Calculates start and end time based on interval
 * @param interval interval string
 * @param rounding rounding in seconds
 * @returns start and end time in unix timestamp format - seconds
 * @example
 * getIntervalTime("15min") // {startTime: 1626951900, endTime: 1626953700}
 * getIntervalTime("1hour") // {startTime: 1626948000, endTime: 1626953700}
 */
function getIntervalTime(interval: string , rounding = 60) {
  const date = moment();
  const endTime = date.unix();

  // Parse the interval string using a regular expression
  const match = interval.match(/(\d+)([a-zA-Z]+)/);
  if (!match) {
      throw new Error(`Invalid interval: ${interval}`);
  }

  const amount = parseInt(match[1]);
  const unit = match[2].toLowerCase();

  // Subtract the specified amount of time from the current date
  const startTime = date.subtract(amount, unit as moment.unitOfTime.DurationConstructor).unix();

  if (rounding) {
      const roundedStartTime = Math.floor(startTime / rounding) * rounding;
      const roundedEndTime = Math.ceil(endTime / rounding) * rounding;
      return { startTime: roundedStartTime, endTime: roundedEndTime };
  }
  
  return { startTime, endTime };
}

/**
 * Calculates resolution based on start and end time
 * @param startTime in unix timestamp format - seconds
 * @param endTime in unix timestamp format - seconds
 * @param samples number of samples (default 1000)
 * @returns resolution in seconds - minimum 1 second, rounded to sensible intervals
 */
function getResolution(startTime: number, endTime: number, samples = 1000) {
  // Calculate raw resolution
  const timeDiff = Math.max(endTime - startTime, 1); // Ensure positive time difference
  const rawResolution = timeDiff / samples;
  
  // Common time intervals in seconds
  const intervals = [
    1, 5, 10, 15, 30,                // seconds
    60, 120, 300, 600, 900, 1800, // minutes (1, 2, 5, 10, 15, 30)
    3600, 7200, 10800, 21600, 43200, // hours (1, 2, 3, 6, 12)
    86400, 172800, 604800            // days (1, 2, 7)
  ];
  
  // Find the smallest interval that is >= raw resolution
  for (const interval of intervals) {
    if (interval >= rawResolution) {
      return interval;
    }
  }
  
  // If resolution is larger than our largest predefined interval,
  // round up to the nearest day
  return Math.ceil(rawResolution / 86400) * 86400;
}

function getAggregationPeriod(aggregationPeriod: string): number {
  // const currentOffsetInMinutes = moment().utcOffset();
  // timestamp += currentOffsetInMinutes*60*1000;
  const periods = {
    "15 Minutes": 15 * 60,
    "1 Hour": 60 * 60,
    "1 Day": 24 * 60 * 60,
    "1 Week": 7 * 24 * 60 * 60,
    "1 Month": 30 * 24 * 60 * 60
  };

  const period = periods[aggregationPeriod as keyof typeof periods] || periods["15 Minutes"]; // Default to 15 minutes
  return period;
}

type UtilizationUnitType = "Byte" | "Bytes" | "KB" | "MB" | "GB" | "TB";

/**
 * Converts a value from bytes to the optimum value and unit.
 * @param {number} bytes - The value to be converted.
 * @returns {value: number, unit: UtilizationUnitType} - The converted value and its unit.
 * @description
 *   - Calculates the index optimum unit using log base 1024.
 *   - It them converts the byte value to the value in the optimum value
 */
function getFormattedBytes(bytes: number): { value: number; unit: UtilizationUnitType } {
  if (bytes < 1) return { value: bytes, unit: "Bytes" };
  else if(bytes === 1) return { value: 1, unit: "Byte" };

  const k = 1024;
  const sizes: UtilizationUnitType[] = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));

  return { value: value, unit: sizes[i] };
}

export const metricHelper = {
  getIntervalTime,
  getResolution,
  getAggregationPeriod,
  getFormattedBytes,
};
